import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import { Serializer } from "survey-core";
import "survey-core/survey-core.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import themeJson from './surveyTheme';

// Theming options - import all themes from survey-core and survey-creator-core
import SurveyTheme from "survey-core/themes";
import SurveyCreatorTheme from "survey-creator-core/themes";
import { registerSurveyTheme, registerCreatorTheme } from "survey-creator-core";

// Register survey themes and creator themes for the Theme Editor tab
registerSurveyTheme(SurveyTheme);
registerCreatorTheme(SurveyCreatorTheme);
/**
 * SurveyJSFormEditor - Editor for non-LHC form schemas (admin use)
 * Uses SurveyJS Creator for visual form building
 *
 * @param {Object} schema - Initial schema object
 * @param {Object} theme - Initial theme object (optional, separate from schema)
 * @param {boolean} previewMode - If true, opens in preview/test tab
 * @param {React.Ref} ref - Ref to expose getSchema and getTheme methods
 */
const SurveyJSFormEditor = React.forwardRef(({ schema, theme, previewMode = false }, ref) => {
  const [creator, setCreator] = useState(null);
  const isUpdatingCreator = useRef(false);

  // Expose getSchema and getTheme methods to parent component
  useImperativeHandle(ref, () => ({
    getSchema: () => {
      return creator ? creator.JSON : {};
    },
    getTheme: () => {
      return creator ? creator.theme : null;
    }
  }), [creator]);

  // Register custom dropdown properties for autocomplete functionality
  useEffect(() => {
    // Custom API URL for autocomplete dropdowns
    Serializer.addProperty("dropdown", {
      name: "customApiUrl:string",
      category: "general",
      visibleIndex: 10
    });

    // Path to extract data array from API response
    Serializer.addProperty("dropdown", {
      name: "customApiResponsePath:string",
      category: "general",
      visibleIndex: 11
    });

    // Field to use as value in dropdown options
    Serializer.addProperty("dropdown", {
      name: "customApiValueField:string",
      category: "general",
      visibleIndex: 12
    });

    // Field to use as display text in dropdown options
    Serializer.addProperty("dropdown", {
      name: "customApiTitleField:string",
      category: "general",
      visibleIndex: 13
    });

    // Minimum characters before triggering search
    Serializer.addProperty("dropdown", {
      name: "customMinSearchLength:number",
      category: "general",
      visibleIndex: 14,
      default: 3
    });
  }, []);

  // Initialize Survey Creator (only once on mount)
  useEffect(() => {
    const creatorOptions = {
      showLogicTab: true,
      showThemeTab: true,
      isAutoSave: false,
      showPreviewTab: true
    };

    const newCreator = new SurveyCreator(creatorOptions);

    // Set theme: use passed theme prop, or fallback to default themeJson
    if (theme) {
      newCreator.theme = theme;
    } else if (themeJson) {
      newCreator.theme = themeJson;
    }

    // If preview mode, switch to preview tab after creator is ready
    if (previewMode) {
      newCreator.activeTab = "test";
    }

    setCreator(newCreator);

    // Cleanup on unmount
    return () => {
      if (newCreator) {
        newCreator.dispose();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only initialize once

  // Load schema whenever it changes or creator becomes available
  useEffect(() => {
    if (!creator) return;

    // Only load if schema is provided and not empty
    if (schema && Object.keys(schema).length > 0) {
      isUpdatingCreator.current = true;
      creator.JSON = schema;
      isUpdatingCreator.current = false;
    }
  }, [schema, creator]);

  return (
    <div className="">
      {/* <h5 className="mb-3">Form Builder</h5> */}
      {creator ? (
        <div style={{ height: '100vh' }}>
          <SurveyCreatorComponent creator={creator} />
        </div>
      ) : (
        <div className="d-flex justify-content-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading Form Builder...</span>
          </div>
        </div>
      )}
    </div>
  );
});

SurveyJSFormEditor.displayName = 'SurveyJSFormEditor';

export default SurveyJSFormEditor;
